import { httpsCallable, functions } from '@/lib/functions';

export type FlightSearchParams = {
  originLocationCode: string;
  destinationLocationCode: string;
  departureDate: string; // YYYY-MM-DD
  returnDate?: string; // YYYY-MM-DD
  adults: number;
  children?: number;
  infants?: number;
  travelClass?: 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST';
  maxPrice?: number;
  currencyCode?: string;
};

export type FlightSegment = {
  departure: {
    iataCode: string;
    at: string;
  };
  arrival: {
    iataCode: string;
    at: string;
  };
  carrierCode: string;
  number: string;
  aircraft?: {
    code: string;
  };
  duration: string;
  numberOfStops: number;
};

export type FlightItinerary = {
  duration: string;
  segments: FlightSegment[];
};

export type FlightOffer = {
  id: string;
  source: string;
  bookingLink?: string;
  redirectRequired?: boolean;
  providerTag?: string;
  instantTicketingRequired: boolean;
  nonHomogeneous: boolean;
  oneWay: boolean;
  lastTicketingDate: string;
  numberOfBookableSeats: number;
  itineraries: FlightItinerary[];
  price: {
    currency: string;
    total: string;
    base: string;
    fees: Array<{
      amount: string;
      type: string;
    }>;
    grandTotal: string;
  };
  pricingOptions: {
    fareType: string[];
    includedCheckedBagsOnly: boolean;
  };
  validatingAirlineCodes: string[];
  travelerPricings: Array<{
    travelerId: string;
    fareOption: string;
    travelerType: string;
    price: {
      currency: string;
      total: string;
      base: string;
      taxes: Array<{
        amount: string;
        type: string;
      }>;
    };
    fareDetailsBySegment: Array<{
      segmentId: string;
      cabin: string;
      fareBasis: string;
      class: string;
      includedCheckedBags: {
        quantity: number;
      };
    }>;
  }>;
};

export type FlightSearchResponse = {
  data: FlightOffer[];
  meta: {
    count: number;
    links: {
      self: string;
    };
  };
  dictionaries?: {
    locations: Record<string, { cityCode: string; countryCode: string }>;
    aircraft: Record<string, string>;
    currencies: Record<string, string>;
    carriers: Record<string, string>;
  };
};

/** Non-Amadeus: no `AFFILIATE_FLIGHTS_SEARCH_URL` and `ENABLE_GOOGLE_TRAVEL_FALLBACK` is off. */
export const META_TRAVEL_FLIGHTS_UNCONFIGURED = 'travel_search_flights_unconfigured';
/** Non-Amadeus: affiliate flight search returned no offers (or failed) and Google fallback is off. */
export const META_TRAVEL_FLIGHTS_NO_OFFERS = 'travel_search_flights_no_offers';
/** Round trip returned no rows from fare partners; results are outbound one-way only (common for long trips). */
export const META_AFFILIATE_FLIGHTS_ONE_WAY_FALLBACK = 'affiliate_flights_one_way_fallback';
/** Non-Amadeus: no `AFFILIATE_HOTELS_SEARCH_URL` and `ENABLE_GOOGLE_TRAVEL_FALLBACK` is off. */
export const META_TRAVEL_HOTELS_UNCONFIGURED = 'travel_search_hotels_unconfigured';
/** Non-Amadeus: affiliate hotel search returned no offers (or failed) and Google fallback is off. */
export const META_TRAVEL_HOTELS_NO_OFFERS = 'travel_search_hotels_no_offers';

export function messageForEmptyFlightSearch(metaSelf?: string): string {
  if (metaSelf === META_TRAVEL_FLIGHTS_UNCONFIGURED) {
    return "We couldn't load flight prices right now. Please try again later. If this keeps happening, the app may need an update.";
  }
  return (
    'No flights matched this search. Try other dates, nearby airports, or cabin class. ' +
    'Our fare feed can omit some routes or return no results for a while—even when travel is soon.'
  );
}

export function messageForEmptyHotelSearch(metaSelf?: string): string {
  if (metaSelf === META_TRAVEL_HOTELS_UNCONFIGURED) {
    return "We couldn't load hotel prices right now. Please try again later. If this keeps happening, the app may need an update.";
  }
  return (
    'No hotels matched this search. Try other dates, a wider area, or star rating. ' +
    'Our provider may have no inventory for this stay—even when check-in is soon.'
  );
}

const callSearchFlights = httpsCallable<FlightSearchParams, FlightSearchResponse>(
  functions,
  'searchFlights'
);

/** Client stops waiting after this so loading never hangs; backend has 60s, we fail a bit earlier for quicker feedback. */
const SEARCH_TIMEOUT_MS = 45_000;

function withSearchTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(message)), timeoutMs)
    ),
  ]);
}

/**
 * Search for flights using the travel provider API
 */
export const searchFlights = async (
  params: FlightSearchParams
): Promise<FlightSearchResponse> => {
  try {
    // Validate params before sending
    if (!params.originLocationCode || params.originLocationCode.length !== 3) {
      throw new Error('Invalid origin airport code. Please select a valid airport.');
    }
    if (!params.destinationLocationCode || params.destinationLocationCode.length !== 3) {
      throw new Error('Invalid destination airport code. Please select a valid airport.');
    }
    if (!params.departureDate || !/^\d{4}-\d{2}-\d{2}$/.test(params.departureDate)) {
      throw new Error('Invalid departure date format.');
    }
    if (params.adults < 1) {
      throw new Error('At least 1 adult traveler is required.');
    }
    
    const response = await withSearchTimeout(
      callSearchFlights(params),
      SEARCH_TIMEOUT_MS,
      'The search is taking longer than expected. Please try again or use different dates or airports.'
    );
    return response.data;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    const code = String(error?.code || 'unknown');
    console.error('[TravelProvider] Error searching flights', { code, message });
    
    // Check for backend/server errors first (500, 502, 503, 504)
    const errorMessage = error?.message || '';
    const errorCode = error?.code || '';
    const isServerError = 
      errorMessage.includes('500') || 
      errorMessage.includes('502') ||
      errorMessage.includes('503') ||
      errorMessage.includes('504') ||
      errorMessage.includes('Internal Server Error') ||
      errorMessage.includes('Bad Gateway') ||
      errorMessage.includes('Service Unavailable') ||
      errorMessage.includes('Gateway Timeout') ||
      errorCode === 'functions/internal' ||
      errorCode === 'functions/deadline-exceeded' ||
      errorCode === 'unavailable';
    
    // Handle specific Firebase error codes
    if (errorCode === 'functions/unavailable' || errorCode === 'unavailable') {
      throw new Error(
        'Flight search service is temporarily unavailable. Please check your internet connection and try again in a moment.'
      );
    }
    
    if (errorCode === 'functions/internal' || isServerError) {
      throw new Error(
        'The flight search service is experiencing a server error. This is usually a temporary issue. Please wait a moment and try again. If the problem persists, please check back later.'
      );
    }
    
    if (errorCode === 'functions/deadline-exceeded') {
      throw new Error(
        'The search is taking longer than expected. Please try again with a simpler search or different dates.'
      );
    }
    
    if (errorCode === 'invalid-argument' || errorCode === 'functions/invalid-argument') {
      throw new Error(error.message || 'Invalid search parameters. Please check your flight details and try again.');
    }
    
    // Handle network errors
    if (errorMessage.toLowerCase().includes('network') || 
        errorMessage.toLowerCase().includes('fetch') ||
        errorMessage.toLowerCase().includes('connection') ||
        errorCode === 'unavailable') {
      throw new Error(
        'Unable to connect to the flight search service. Please check your internet connection and try again.'
      );
    }
    
    // Generic error handling
    if (error?.message) {
      throw new Error(error.message);
    }
    
    throw new Error(
      'Failed to search for flights. Please verify your search criteria and try again. If the problem persists, the service may be temporarily unavailable.'
    );
  }
};

/**
 * Format flight duration for display
 */
export const formatFlightDuration = (duration: string): string => {
  // Duration is in PT format (e.g., "PT2H30M" or "PT15H45M")
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return duration;

  const hours = match[1] ? parseInt(match[1], 10) : 0;
  const minutes = match[2] ? parseInt(match[2], 10) : 0;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  }
  return duration;
};

/**
 * Format date/time for display
 */
export const formatFlightDateTime = (dateTime: string): { date: string; time: string } => {
  const date = new Date(dateTime);
  return {
    date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
};

// Hotel Search Types
export type HotelSearchParams = {
  cityCode: string;
  checkInDate: string; // YYYY-MM-DD
  checkOutDate: string; // YYYY-MM-DD
  adults: number;
  roomQuantity?: number;
  priceRange?: {
    min?: number;
    max?: number;
  };
  ratings?: number[]; // e.g., [3, 4, 5] for 3-5 star hotels
  currencyCode?: string;
};

export type HotelOffer = {
  type: string;
  bookingLink?: string;
  redirectRequired?: boolean;
  providerTag?: string;
  hotel: {
    hotelId: string;
    name: string;
    rating?: number;
    description?: {
      lang: string;
      text: string;
    };
    amenities?: string[];
    contact?: {
      phone: string;
      fax?: string;
    };
    address: {
      lines: string[];
      postalCode?: string;
      cityName: string;
      countryCode: string;
      stateCode?: string;
    };
    geoCode?: {
      latitude: number;
      longitude: number;
    };
  };
  offers: Array<{
    id: string;
    checkInDate: string;
    checkOutDate: string;
    room: {
      type: string;
      typeEstimated?: {
        category: string;
        beds: number;
        bedType: string;
      };
      description?: {
        text: string;
        lang: string;
      };
    };
    guests: {
      adults: number;
    };
    price: {
      currency: string;
      total: string;
      base?: string;
      variations?: {
        average?: {
          base: string;
        };
        changes?: Array<{
          startDate: string;
          endDate: string;
          total: string;
        }>;
      };
    };
    policies?: {
      paymentType: string;
      cancellation?: {
        type: string;
        amount?: string;
        numberOfNights?: number;
        deadline?: string;
      };
    };
    self?: string;
  }>;
};

export type HotelSearchResponse = {
  data: HotelOffer[];
  meta: {
    count: number;
    links: {
      self: string;
    };
  };
};

// Flight Booking Types
export type FlightBookingParams = {
  flightOffer: FlightOffer;
  travelers: Array<{
    id: string;
    dateOfBirth: string; // YYYY-MM-DD
    name: {
      firstName: string;
      lastName: string;
    };
    gender: "MALE" | "FEMALE";
    contact: {
      emailAddress: string;
      phones: Array<{
        deviceType: "MOBILE" | "LANDLINE";
        countryCallingCode: string;
        number: string;
      }>;
    };
    documents?: Array<{
      documentType: string;
      birthPlace?: string;
      issuanceLocation?: string;
      issuanceDate?: string;
      number: string;
      expiryDate: string;
      issuanceCountry: string;
      validityCountry: string;
      nationality: string;
      holder: boolean;
    }>;
  }>;
  remarks?: {
    general?: Array<{
      subType: string;
      text: string;
    }>;
  };
  ticketingAgreement?: {
    option: string;
    delay: string;
  };
  contacts?: Array<{
    addresseeName: {
      firstName: string;
      lastName: string;
    };
    companyName?: string;
    purpose: string;
    phones: Array<{
      deviceType: "MOBILE" | "LANDLINE";
      countryCallingCode: string;
      number: string;
    }>;
    emailAddress: string;
    address?: {
      lines: string[];
      postalCode: string;
      cityName: string;
      countryCode: string;
    };
  }>;
};

export type FlightBookingResponse = {
  data: {
    type: string;
    id: string;
    associatedRecords?: Array<{
      reference: string;
      creationDate: string;
      originSystemCode: string;
      flightOfferId?: string;
    }>;
    flightOffers: FlightOffer[];
    travelers: Array<{
      id: string;
      dateOfBirth: string;
      name: {
        firstName: string;
        lastName: string;
      };
      contact: {
        emailAddress: string;
        phones: Array<{
          deviceType: string;
          countryCallingCode: string;
          number: string;
        }>;
      };
    }>;
    ticketingAgreement?: {
      option: string;
      delay: string;
    };
  };
};

// Hotel Booking Types
export type HotelBookingParams = {
  offerId: string;
  guests: Array<{
    name: {
      title: "MR" | "MRS" | "MS" | "MISS";
      firstName: string;
      lastName: string;
    };
    contact: {
      phone: string;
      email: string;
    };
  }>;
  payments?: Array<{
    method: string;
    card?: {
      vendorCode: string;
      cardNumber: string;
      expiryDate: string;
    };
  }>;
};

export type HotelBookingResponse = {
  data: {
    type: string;
    id: string;
    associatedRecords?: Array<{
      reference: string;
      creationDate: string;
      originSystemCode: string;
    }>;
    hotel?: {
      hotelId: string;
      name: string;
    };
    offers?: Array<{
      id: string;
      checkInDate: string;
      checkOutDate: string;
      room: {
        type: string;
      };
      guests: {
        adults: number;
      };
      price: {
        currency: string;
        total: string;
      };
    }>;
    guests?: Array<{
      name: {
        title: string;
        firstName: string;
        lastName: string;
      };
      contact: {
        phone: string;
        email: string;
      };
    }>;
  };
};

const callSearchHotels = httpsCallable<HotelSearchParams, HotelSearchResponse>(
  functions,
  'searchHotels'
);

const callBookFlight = httpsCallable<FlightBookingParams, FlightBookingResponse>(
  functions,
  'bookFlight'
);

const callBookHotel = httpsCallable<HotelBookingParams, HotelBookingResponse>(
  functions,
  'bookHotel'
);

/**
 * Search for hotels using the travel provider API
 */
export const searchHotels = async (
  params: HotelSearchParams
): Promise<HotelSearchResponse> => {
  try {
    if (!params.cityCode || params.cityCode.length !== 3) {
      throw new Error('Invalid city code. Please select a valid city.');
    }
    if (!params.checkInDate || !/^\d{4}-\d{2}-\d{2}$/.test(params.checkInDate)) {
      throw new Error('Invalid check-in date format.');
    }
    if (!params.checkOutDate || !/^\d{4}-\d{2}-\d{2}$/.test(params.checkOutDate)) {
      throw new Error('Invalid check-out date format.');
    }
    if (params.adults < 1) {
      throw new Error('At least 1 adult is required.');
    }
    
    const response = await withSearchTimeout(
      callSearchHotels(params),
      SEARCH_TIMEOUT_MS,
      'The search is taking longer than expected. Please try again or use different dates or city.'
    );
    return response.data;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[TravelProvider] Error searching hotels', { message });
    throw error;
  }
};

/**
 * Book a flight using the travel provider API
 */
export const bookFlight = async (
  params: FlightBookingParams
): Promise<FlightBookingResponse> => {
  try {
    const response = await callBookFlight(params);
    return response.data;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[TravelProvider] Error booking flight', { message });
    throw error;
  }
};

/**
 * Book a hotel using the travel provider API
 */
export const bookHotel = async (
  params: HotelBookingParams
): Promise<HotelBookingResponse> => {
  try {
    const response = await callBookHotel(params);
    return response.data;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[TravelProvider] Error booking hotel', { message });
    throw error;
  }
};

